import { FC, useEffect, useRef } from 'react';
import { CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Alert } from '@components/Alert';
import {
  useResendEmailVerification,
  useVerifyEmail,
} from '@modules/auth/queries';
import { useAuthStore } from '@modules/auth/store/auth-store';

import { AccountCreated } from './AccountCreated';
import { TokenExpired } from './TokenExpired';

export const Verify: FC = () => {
  const verificationAttempted = useRef(false);
  const { t } = useTranslation();
  const resendMutation = useResendEmailVerification();
  const verifyMutation = useVerifyEmail();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Get state and actions from Zustand
  const {
    userInfo,
    verificationError,
    isReadyToLogin,
    isEmailVerified,
    resendVerificationEmail,
  } = useAuthStore();

  const email = userInfo?.email;
  const shouldRedirectToSignup = !email && !token;

  // Move the redirect effect to the top level
  useEffect(() => {
    // Only redirect if verification was successful
    if (isEmailVerified || isReadyToLogin) {
      const redirectTimer = setTimeout(() => {
        window.location.href = '/auth/login';
      }, 3000);

      return () => clearTimeout(redirectTimer);
    }
  }, [isEmailVerified, isReadyToLogin]);

  useEffect(() => {
    // Verify email when token is present and we haven't attempted verification yet
    if (token && !verificationAttempted.current && !shouldRedirectToSignup) {
      verificationAttempted.current = true;

      // No need to set local state, the mutation will update store state
      verifyMutation.mutate({ token });
    }
  }, [token, verifyMutation, shouldRedirectToSignup]);

  const handleResendEmail = async () => {
    if (!userInfo?.email) return;

    try {
      await resendMutation.mutateAsync({ email: userInfo.email });
    } catch {
      // Ignoring error; handled by UI re-trying the request
    }

    resendVerificationEmail();
  };

  // Render different content based on verification status from Zustand store
  const renderVerificationContent = () => {
    // completed registration - redirected to this view
    if (email && !token) {
      return <AccountCreated email={email} onResend={handleResendEmail} />;
    }
    // email verification success - redirected to this view
    else if (isEmailVerified || isReadyToLogin) {
      return (
        <CardContent sx={styles.cardContent}>
          <Alert>{t('auth.verify.success')}</Alert>
        </CardContent>
      );
    } else if (verificationError && userInfo?.email) {
      return <TokenExpired onResend={handleResendEmail} />;
    }
    return null;
  };

  // If no email in store and no token, redirect to signup
  if (shouldRedirectToSignup) {
    return <Navigate to="/auth/sign-up" />;
  }

  return renderVerificationContent();
};

const styles = {
  cardContent: {
    marginTop: '1.25rem',
  },
};
